import { Link } from "react-router-dom";
import type { Report } from "../types";
import { formatFeaturedDivergence, formatProbabilityLabel } from "../lib/format";
import { mergeReportHistory } from "../lib/reportHistory";
import { estimateLabel } from "../lib/reportView";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { EvidenceBalanceBadge } from "./EvidenceBalanceBadge";
import { MarketContext } from "./MarketContext";
import { FeaturedHistoryChart } from "./FeaturedHistoryChart";

export function FeaturedReportSurface({ report }: { report: Report }) {
  const history = mergeReportHistory(report);
  const publishedLabel = report.publishedAt ? new Date(report.publishedAt).toLocaleDateString("en", { month: "short", day: "numeric" }).toUpperCase() : "CURRENT";
  const reviewedLabel = formatReviewedAt(report.lastReviewedAt ?? report.publishedAt);

  return (
    <section className="site-shell section-block home-featured" aria-labelledby="featured-report-heading">
      <div className="home-featured-context"><p className="eyebrow">FEATURED REPORT · {publishedLabel}</p></div>
      <article className="home-featured-surface">
        <div className="home-featured-heading">
          <div>
            <div className="home-featured-meta"><span>{report.category ?? "Report"}</span><span>{report.platforms[0]?.name ?? "Market context"}</span></div>
            <h3 id="featured-report-heading">{report.title}</h3>
            <MarketContext report={report} />
          </div>
          <div className="home-featured-published"><span>Published</span><strong>{report.publishedAt ? new Date(report.publishedAt).toLocaleDateString("en", { month: "short", day: "numeric" }) : "—"}</strong></div>
        </div>

        <div className="home-featured-intelligence">
          <div className="home-featured-chart-wrap">
            {history.length ? <FeaturedHistoryChart history={history} currentEvidenceBalance={report.evidenceBalance} /> : <div className="home-history-unavailable"><span className="eyebrow">HISTORY</span><strong>No reviewed history yet.</strong><p>FTM does not draw a line until independent estimate observations exist.</p></div>}
          </div>
          <div className="home-featured-range" data-testid="featured-current-summary">
            <div className="home-value-grid">
              <FeaturedValue label="Market" value={report.marketProbability === null || report.marketProbability === undefined ? "Unavailable" : formatProbabilityLabel(report.marketProbability)} />
              <FeaturedValue label="FTM Estimate" value={estimateLabel(report)} brand />
              <FeaturedValue label="Divergence" value={report.divergenceLow !== null && report.divergenceLow !== undefined && report.divergenceHigh !== null && report.divergenceHigh !== undefined ? formatFeaturedDivergence(report.divergenceLow, report.divergenceHigh) : "Unavailable"} divergence />
            </div>
            <div className="home-featured-summary-meta">
              <div className="home-featured-summary-item"><span>Evidence balance</span><div><EvidenceBalanceBadge value={report.evidenceBalance} /><ConfidenceBadge value={report.confidence} /></div></div>
              <div className="home-featured-summary-item"><span>Last reviewed</span><strong>{reviewedLabel}</strong></div>
            </div>
            <Link className="button-primary home-featured-snapshot-cta" to={`/reports/${report.slug}`}>Read the Featured Report <span aria-hidden="true">→</span></Link>
          </div>
        </div>

        <div className="home-featured-footer">
          <div className="home-featured-evidence">
            <div className="home-evidence-heading"><span>Evidence context</span></div>
            <p>{report.shortReasons[0] ?? report.whyFtmDiffers ?? "The evidence record is available inside the full Report."}</p>
          </div>
        </div>
      </article>
    </section>
  );
}

function formatReviewedAt(value?: string | null): string {
  if (!value) return "Not yet reviewed";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not yet reviewed";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York", timeZoneName: "short" }).format(date);
}

function FeaturedValue({ label, value, brand = false, divergence = false }: { label: string; value: string; brand?: boolean; divergence?: boolean }) {
  return <div className={`home-featured-value ${brand ? "home-featured-value-brand" : ""} ${divergence ? "home-featured-value-divergence" : ""}`}><span>{label}</span><strong>{value}</strong></div>;
}
